"use client";

import { CheckCircle2, KeyRound, RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  addPortalPasskey,
  portalPasskeysSupported,
} from "@/lib/portal-passkey-client";

type EnrollmentState = "checking" | "hidden" | "ready" | "pending" | "saved";

export function PasskeyEnrollmentBanner({ hasPasskey }: { hasPasskey: boolean }) {
  const [state, setState] = useState<EnrollmentState>(
    hasPasskey ? "hidden" : "checking",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasPasskey) {
      return;
    }

    let cancelled = false;
    void portalPasskeysSupported()
      .then((supported) => {
        if (!cancelled) {
          setState(supported ? "ready" : "hidden");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState("hidden");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasPasskey]);

  useEffect(() => {
    function handlePasskeysChanged() {
      setState("hidden");
    }

    window.addEventListener("portal-passkeys-changed", handlePasskeysChanged);
    return () => {
      window.removeEventListener("portal-passkeys-changed", handlePasskeysChanged);
    };
  }, []);

  async function addPasskey() {
    setState("pending");
    setError(null);

    try {
      await addPortalPasskey();
      setState("saved");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unable to save that passkey.");
      setState("ready");
    }
  }

  if (state === "checking" || state === "hidden") {
    return null;
  }

  if (state === "saved") {
    return (
      <div className="border-b border-success/30 bg-success/10">
        <div className="mx-auto flex max-w-[1680px] items-center gap-2 px-4 py-2 text-sm text-foreground sm:px-6 lg:px-8">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
          <span className="font-medium">Passkey saved.</span>
          <Button
            className="ml-auto h-7 px-2 text-foreground hover:bg-success/10"
            onClick={() => setState("hidden")}
            size="sm"
            type="button"
            variant="ghost"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-border/60 bg-card/55">
      <div className="mx-auto flex max-w-[1680px] flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <KeyRound className="h-4 w-4 shrink-0 text-primary" />
          <span className="font-medium text-foreground">Add a passkey</span>
          {error ? (
            <span className="truncate text-muted-foreground">{error}</span>
          ) : (
            <span className="text-muted-foreground">Use system sign-in next time.</span>
          )}
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <Button
            className="h-8"
            disabled={state === "pending"}
            onClick={addPasskey}
            size="sm"
            type="button"
          >
            {state === "pending" ? (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <KeyRound className="mr-1.5 h-3.5 w-3.5" />
            )}
            Save passkey
          </Button>
          <Button
            className="h-8"
            disabled={state === "pending"}
            onClick={() => setState("hidden")}
            size="sm"
            type="button"
            variant="ghost"
          >
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}
